import { IIconProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLink, IPPrimaryButton, IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPFacepile, IPFacepilePersona, IPOverflowButtonType } from 'baseComponents/Facepile/IPFacepile';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { DialogOk } from 'components/Dialogs/DialogOk';
import { globalStackTokensGapLarge, globalStackTokensGapMedium, globalTextStylesBold } from 'globalStyles';
import { WidgetTenantOverviewDTO } from 'models/dto/widgets/widgetTenantOverviewDTO';
import { DirectoryRef, hasEntraDirectory, IdentityProviderType } from 'models/identityProvider';
import { CustomerAccessStatus } from 'models/tenant';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWidgetDataTenantOverview } from 'services/Api/dashboardService';
import { apiGetPartnerCustomerAccess } from 'services/Api/partnerService';
import { apiRequest } from 'services/Auth/authConfig';
import { fromApiDateTimeToDateOptional, toLocaleDateShort } from 'utils/datetime';
import { toNameInitial } from 'utils/string';
import { navigateToExternalUrl } from 'utils/url';
import { IWidgetRendererProps } from '../WidgetRenderer';

const signinIcon: IIconProps = { iconName: 'Signin' };

//the customer's directory as the deep link needs it; the DTO predates identity providers, so a missing type
//reads as Entra
const getCustomerDirectory = (data: WidgetTenantOverviewDTO): DirectoryRef => ({
  identityProviderType: data.identityProviderType ?? IdentityProviderType.Entra,
  providerTenantId: data.providerTenantId,
});

interface IWidgetTenantOverviewProps extends IWidgetRendererProps {}

const WidgetTenantOverview = (props: IWidgetTenantOverviewProps) => {
  const { t } = useTranslation(['translation', 'widgets', 'partnerAdmin']);
  const appContext = useContext(AppContext);
  const [data, setData] = useState<WidgetTenantOverviewDTO | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<CustomerAccessStatus | undefined>(undefined);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isLoading) return;
        setIsLoading(true);

        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const result = await apiGetWidgetDataTenantOverview(accessToken);
        setData(result);
      } catch (err) {
        appContext.setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.widget]);

  //Validate access first (impersonation rules in PrincipalAuthValidator); on success open the customer
  //tenant in a new tab (the ?tid bootstrap logs into that tenant). A real sign-in at the customer's directory
  //(the Entra B2B guest route), so it exists only for a customer with an Entra directory.
  const loginToCustomer = async () => {
    if (!data?.tenantId || !hasEntraDirectory(getCustomerDirectory(data))) return;

    try {
      appContext.showContentLoader();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const status = await apiGetPartnerCustomerAccess(data.tenantId, accessToken);
      if (status !== CustomerAccessStatus.Allowed) {
        setAccessDeniedStatus(status);

        return;
      }

      //the customer's ISOPlanner tenant id, not an empty string: an empty org unit makes the bootstrap store the
      //directory id as the org unit, which the back-end cannot resolve, and every request then 401s
      navigateToExternalUrl('/', getCustomerDirectory(data), data.tenantId);
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
    }
  };

  const getAccessDeniedSubText = (): string => {
    if (accessDeniedStatus === CustomerAccessStatus.NoPaidLicenseAtPartner) {
      return t('partnerAdmin:AccessStatus.NoPaidLicenseAtPartner');
    }

    if (accessDeniedStatus === CustomerAccessStatus.NoLicenseAtCustomer) {
      return t('partnerAdmin:AccessStatus.NoLicenseAtCustomer');
    }

    return '';
  };

  //
  // Render
  //
  if (isLoading || !data) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  const lastLogin = fromApiDateTimeToDateOptional(data.lastLogin);
  const customerDirectory = getCustomerDirectory(data);

  const renderStat = (value: string, label: string) => {
    return (
      <IPStack horizontalAlign="center" tokens={{ childrenGap: 4 }} styles={{ root: { minWidth: 90 } }}>
        <IPText variant="xxLarge" styles={globalTextStylesBold}>
          {value}
        </IPText>
        <IPText variant="small">{label}</IPText>
      </IPStack>
    );
  };

  const renderAdmins = () => {
    if (!data.admins || data.admins.length === 0) {
      return <IPText variant="small">{t('widgets:TenantOverview.NoAdministrators')}</IPText>;
    }

    // 3+ admins do not fit as full personas in the card; show a compact facepile (click an avatar to email).
    if (data.admins.length >= 3) {
      const personas: IPFacepilePersona[] = data.admins.map((admin) => ({
        personaName: admin.name,
        imageInitials: toNameInitial(admin.name),
        onClick: () => {
          window.location.href = 'mailto:' + admin.email;
        },
      }));

      return (
        <IPFacepile
          personas={personas}
          personaSize={IPPersonaSize.size32}
          maxDisplayablePersonas={5}
          overflowButtonType={IPOverflowButtonType.descriptive}
        />
      );
    }

    return (
      <IPStack horizontal tokens={globalStackTokensGapLarge}>
        {data.admins.map((admin, idx) => (
          <IPStack.Item key={idx}>
            <IPPersona
              size={IPPersonaSize.size40}
              text={admin.name}
              imageInitials={toNameInitial(admin.name)}
              onRenderSecondaryText={() => <IPLink href={'mailto:' + admin.email}>{admin.email}</IPLink>}
            />
          </IPStack.Item>
        ))}
      </IPStack>
    );
  };

  return (
    <>
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPStack horizontal wrap horizontalAlign="space-around" tokens={globalStackTokensGapLarge}>
            {renderStat(data.userCount.toString(), t('widgets:TenantOverview.Users'))}
            {renderStat(data.monthlyActiveUsers.toString(), t('widgets:TenantOverview.ActiveUsers'))}
            {renderStat(`${data.usedMgmtLicenses} / ${data.mgmtLicenses}`, t('widgets:TenantOverview.Licenses'))}
            {renderStat(lastLogin ? toLocaleDateShort(lastLogin) : '-', t('widgets:TenantOverview.LastLogin'))}
          </IPStack>
        </IPStack.Item>
        {hasEntraDirectory(customerDirectory) && (
          <IPStack.Item>
            <IPPrimaryButton iconProps={signinIcon} onClick={loginToCustomer}>
              {t('partnerAdmin:Actions.LoginToCustomer')}
            </IPPrimaryButton>
          </IPStack.Item>
        )}
        <IPStack.Item>
          <IPSeparator />
          <IPText variant="smallPlus" styles={globalTextStylesBold}>
            {t('widgets:TenantOverview.Administrators')}
          </IPText>
        </IPStack.Item>
        <IPStack.Item grow styles={{ root: { overflowY: 'auto', minHeight: 0 } }}>
          {renderAdmins()}
        </IPStack.Item>
      </IPStack>
      <DialogOk
        hidden={accessDeniedStatus === undefined}
        onOk={() => setAccessDeniedStatus(undefined)}
        title={t('partnerAdmin:AccessStatus.Title')}
        subText={getAccessDeniedSubText()}
      />
    </>
  );
};

export default WidgetTenantOverview;
